'use client'

/* eslint-disable jsx-a11y/media-has-caption */
import React, { useEffect, useState } from 'react'
import { ga4Event } from '@/libs/Google/GoogleTagManager.lib'
import { formatTime, getPercentage } from '@/utils/timeUtils'
import { Icon } from '../Icon'
import { Typography } from '../Typography'
import styles from './AudioPlayer.module.scss'

const AudioPlayer = ({
  id,
  title,
  audioUrl,
  mediaType,
  section,
  audioDur,
  show,
}: {
  id: string
  title: string
  audioUrl: string
  mediaType: 'voice_player' | 'audio_player' | 'livestream_player'
  section: string
  audioDur?: string
  show?: boolean
}) => {
  const [audioPlayer, setAudioPlayer] = useState<HTMLAudioElement | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isStarted, setIsStarted] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [percentage, setPercentage] = useState(0)
  const [volume, setVolume] = useState(100)
  const [savedVolume, setSavedVolume] = useState(1)
  const [isMuted, setIsMuted] = useState(false)
  const [speed, setSpeed] = useState(1)
  const [displayed, setDisplayed] = useState(false)

  const trackAudio = (event_name: string) => {
    ga4Event({
      event_name: event_name,
      media_type: mediaType,
      media_view_mode: 'not_available',
      media_headline: title,
      media_id: id,
      media_editorial_unit: section,
      media_speed:
        event_name === 'change_speed_clicked' ||
        event_name === 'content_started' ||
        event_name === 'content_aborted_and_closed' ||
        event_name === 'content_finished'
          ? audioPlayer?.playbackRate
          : 'not_available',
      ...(event_name === 'content_started' ? { media_length: audioDur || 'not_available' } : {}),
      ...(event_name === 'content_aborted_and_closed' || event_name === 'content_finished'
        ? { media_time_spent: Math.round(currentTime) }
        : {}),
    })
  }

  function play() {
    if (audioPlayer) {
      audioPlayer.play()
      setIsPlaying(true)

      if (isStarted) {
        trackAudio('resume_clicked')
      } else {
        trackAudio('content_started')
        setIsStarted(true)
      }
    }
  }

  const pause = () => {
    if (audioPlayer) {
      audioPlayer.pause()
      setIsPlaying(false)
      trackAudio('pause_clicked')
    }
  }

  const updateVolume = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (audioPlayer) {
      const volume = Number.parseInt(e.target.value) / 100
      audioPlayer.volume = volume
      setVolume(Number.parseInt(e.target.value))
    }
  }

  const toggleMute = () => {
    if (audioPlayer) {
      if (!isMuted) {
        setSavedVolume(audioPlayer.volume)
      }
      audioPlayer.muted = !audioPlayer.muted
      setVolume(audioPlayer.muted ? 0 : savedVolume * 100)
      setIsMuted(audioPlayer.muted)
    }
  }

  const updateTime = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (audioPlayer) {
      const time = (Number.parseInt(e.target.value) / 100) * audioPlayer.duration
      audioPlayer.currentTime = time
      setCurrentTime(time)
    }
  }

  const skipBack = () => {
    if (audioPlayer) {
      // Skip back 10 seconds
      audioPlayer.currentTime = Math.max(audioPlayer.currentTime - 10, 0)
      setCurrentTime(audioPlayer.currentTime)
      trackAudio('rewind_clicked')
    }
  }

  const skipForward = () => {
    if (audioPlayer) {
      // Skip forward 10 seconds
      audioPlayer.currentTime = Math.min(audioPlayer.currentTime + 10, audioPlayer.duration)
      setCurrentTime(audioPlayer.currentTime)
      trackAudio('fast_forward_clicked')
    }
  }

  const updateSpeed = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (audioPlayer) {
      const speed = Number.parseFloat(e.target.value)
      audioPlayer.playbackRate = speed
      setSpeed(speed)
      trackAudio('change_speed_clicked')
    }
  }

  useEffect(() => {
    if (show) {
      play()
      if (!displayed) trackAudio('opened')
      setDisplayed(true)
    } else {
      pause()
      if (displayed) trackAudio('closed')
      if (audioPlayer && audioPlayer.currentTime < audioPlayer.duration) trackAudio('content_aborted_and_closed')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])

  useEffect(() => {
    if (audioPlayer) {
      let contentFinished = false
      audioPlayer.addEventListener('timeupdate', () => {
        setCurrentTime(audioPlayer.currentTime)
        const perc = getPercentage(audioPlayer.currentTime, audioPlayer.duration)
        setPercentage(perc)
        if (perc > 97 && !contentFinished) {
          trackAudio('content_finished')
          contentFinished = true
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioPlayer])
  const progressBarStyle = {
    background: `linear-gradient(
      to right,
      var(--black) ${percentage}%,
      rgba(0, 0, 0, 0.1) ${percentage}%
    )`,
  }
  const volumeBarStyle = {
    background: `linear-gradient(
      to right,
      var(--white) ${volume}%,
      rgba(255, 255, 255, 0.5) ${volume}%
    )`,
  }
  return (
    <div className={styles.audio + (show === false ? ' ' + styles.hide : '')} data-testid="audio-player">
      <audio ref={(audio) => setAudioPlayer(audio)} preload="metadata" src={audioUrl} data-testid="audio-element" />
      <div className={styles.progress}>
        <div>
          <Typography as="span" variant="s">
            {formatTime(currentTime)}
          </Typography>{' '}
          /{' '}
          <Typography as="span" variant="s">
            {audioDur}
          </Typography>
        </div>
        <input
          type="range"
          min="0"
          max="100"
          value={percentage}
          onChange={updateTime}
          style={progressBarStyle}
          data-testid="progress-bar"
        />
      </div>
      <div className={styles.controls}>
        <div className={styles.wrapper}>
          <button onClick={isPlaying ? pause : play}>{isPlaying ? <Icon name="pause" /> : <Icon name="play" />}</button>

          <button onClick={skipBack} data-testid="skip-back-button">
            <Icon name="skipBack" />
          </button>

          <button onClick={skipForward} data-testid="skip-forward-button">
            <Icon name="skipForward" />
          </button>

          <div role="button" className={styles.volume}>
            <button onClick={toggleMute} data-testid="mute-button">
              {isMuted ? <Icon name="mute" /> : <Icon name="speaker" />}
            </button>
            <input
              type="range"
              min="0"
              max="100"
              value={volume}
              onChange={updateVolume}
              style={volumeBarStyle}
              data-testid="volume-slider"
            />
          </div>
        </div>

        <button className={styles.speed}>
          <Icon name="speed1x" />
          <ul>
            <li>
              <label htmlFor="05x">
                <input
                  type="radio"
                  value="0.5"
                  id="05x"
                  name="speed"
                  checked={speed === 0.5}
                  onChange={updateSpeed}
                  hidden
                />
                0.5x
                {speed === 0.5 && <Icon name="checkmark" />}
              </label>
            </li>
            <li>
              <label htmlFor="08x">
                <input
                  type="radio"
                  value="0.8"
                  id="08x"
                  name="speed"
                  checked={speed === 0.8}
                  onChange={updateSpeed}
                  hidden
                />
                0.8x
                {speed === 0.8 && <Icon name="checkmark" />}
              </label>
            </li>
            <li>
              <label htmlFor="1x">
                <input
                  type="radio"
                  value="1"
                  id="1x"
                  name="speed"
                  checked={speed === 1}
                  onChange={updateSpeed}
                  hidden
                />
                1.0x
                {speed === 1 && <Icon name="checkmark" />}
              </label>
            </li>
            <li>
              <label htmlFor="12x">
                <input
                  type="radio"
                  value="1.2"
                  id="12x"
                  name="speed"
                  checked={speed === 1.2}
                  onChange={updateSpeed}
                  hidden
                />
                1.2x
                {speed === 1.2 && <Icon name="checkmark" />}
              </label>
            </li>
            <li>
              <label htmlFor="15x">
                <input
                  type="radio"
                  value="1.5"
                  id="15x"
                  name="speed"
                  checked={speed === 1.5}
                  onChange={updateSpeed}
                  hidden
                />
                1.5x
                {speed === 1.5 && <Icon name="checkmark" />}
              </label>
            </li>
            <li>
              <label htmlFor="20x">
                <input
                  type="radio"
                  value="2.0"
                  id="20x"
                  name="speed"
                  checked={speed === 2}
                  onChange={updateSpeed}
                  hidden
                />
                2.0x
                {speed === 2 && <Icon name="checkmark" />}
              </label>
            </li>
          </ul>
        </button>
      </div>
    </div>
  )
}

export default AudioPlayer
